
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Language } from '../types';
import { t } from '../locales';

interface QuantityEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentItem: any;
  onSave: (q: number, u: string) => void;
  language: Language;
  theme?: 'emerald' | 'yellow'; // New theme prop
}

export const QuantityEditModal: React.FC<QuantityEditModalProps> = ({ 
    isOpen, 
    onClose, 
    currentItem, 
    onSave, 
    language,
    theme = 'emerald'
}) => {
    if (!isOpen || !currentItem) return null;
    
    const [qtyStr, setQtyStr] = useState<string>(String(currentItem.quantity || '1'));
    const [unit, setUnit] = useState<string>(currentItem.unit || '');
    
    const isYellow = theme === 'yellow';
    
    const commonUnitKeys = [
        'u_piece', 'u_slice', 'u_serving', 'u_g', 'u_ml', 'u_cup', 
        'u_oz', 'u_tbsp', 'u_tsp', 'u_plate', 'u_bowl', 'u_can', 
        'u_bottle', 'u_spoon', 'u_handful', 'u_glass', 'u_package', 'u_box'
    ];
    
    const handleSave = () => { 
        const val = parseFloat(qtyStr); 
        onSave(isNaN(val) ? 0 : val, unit); 
    };

    const primaryBtnClass = isYellow 
        ? 'bg-yellow-500 hover:bg-yellow-600' 
        : 'bg-emerald-600 hover:bg-emerald-700';

    const focusRingClass = isYellow 
        ? 'focus:ring-yellow-500' 
        : 'focus:ring-emerald-500';

    const activeUnitClass = isYellow
        ? 'bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700'
        : 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700';

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
            <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">{t(language, 'editQuantity')}</h3>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t(language, 'qty')}</label>
                            <input 
                                type="number" 
                                step="0.1" 
                                value={qtyStr} 
                                onChange={(e) => setQtyStr(e.target.value)} 
                                className={`w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 ${focusRingClass} outline-none`} 
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{t(language, 'customUnit')}</label>
                            <input 
                                type="text" 
                                value={unit} 
                                onChange={(e) => setUnit(e.target.value)} 
                                className={`w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 ${focusRingClass} outline-none`} 
                                placeholder="e.g. box" 
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t(language, 'quickSelect')}</label>
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                            {commonUnitKeys.map(key => { 
                                const unitLabel = t(language, key as any) || key.replace('u_', ''); 
                                return (
                                    <button 
                                        key={key} 
                                        onClick={() => setUnit(unitLabel)} 
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${unit === unitLabel ? activeUnitClass : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'}`}
                                    >
                                        {unitLabel}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex gap-3 mt-2">
                        <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">{t(language, 'cancel')}</button>
                        <button onClick={handleSave} className={`flex-1 py-3 rounded-xl text-white font-bold shadow-lg transition-transform active:scale-95 ${primaryBtnClass}`}>{t(language, 'save')}</button>
                    </div>
                </div>
            </div>
        </div>, 
        document.body
    );
};
